import React, { useState } from "react";
import { makeStyles } from "@material-ui/core";
import { TextField, Typography, IconButton, Collapse, Box, Snackbar } from "@mui/material";
import EditExternalApisButton from "./EditExternalApisButton";
import DescriptionItem from "../../../Shared/DescriptionItem";
import { Timer, Braces, Copy, ChevronDown, ChevronUp, CircleHelp, Hash, Link, Eye, Pencil, List, Anchor, Pin, Minus } from "lucide-react";
import { externalApiTypes, externalApiEnumBehaviorTypes } from "../../../../Config/constants";
import "react-toastify/dist/ReactToastify.css";
import * as apiHelper from "../../../..//Utils/API/apiHelper";
import Tooltip from "../../../Shared/Tooltip";
import Button from "../../../Shared/Buttons/Button";
import Label from "../../../Shared/Label";
import { externalApiTableConfig } from "../../../../Config/tableConfig";

function Row(props) {
  const classes = useStyles();
  const row = props.row;
  const [open, setOpen] = useState(false);
  const [apiExamplePoints, setApiExamplePoints] = useState("");
  const [openToast, setOpenToast] = useState(false);
  
  const handleCopyFormat = () => {
    handleOpenToast();
    navigator.clipboard.writeText(apiExamplePoints);
  };

  const handleOpenToast = () => {
    setOpenToast(true);
  };

  const handleCloseToast = (reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToast(false);
  };

  const getApiTypeIcon = (externalApiTypeId) => {
    switch (externalApiTypeId) {
      case externalApiTypes.DATA:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Braces size={16} style={{ marginRight: "8px" }} />
            Data
          </div>
        );
      case externalApiTypes.STOPWATCH:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Timer size={16} style={{ marginRight: "8px" }} />
            Stopwatch
          </div>
        );
      case externalApiTypes.NUMERIC:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Hash size={16} style={{ marginRight: "8px" }} />
            Numeric
          </div>
        );
      case externalApiTypes.ENUM:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <List size={16} style={{ marginRight: "8px" }} />
            Enum
          </div>
        );
      default:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CircleHelp size={16} style={{ marginRight: "8px" }} />
            Unknown
          </div>
        );
    }
  };

  const isAnchorOrSticky = row.externalApiTypeId === externalApiTypes.ENUM &&
    (row.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR ||
     row.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.STICKY);

  const handleOpen = () => {
    if (!isAnchorOrSticky) {
      var apiFormat = JSON.parse(row.apiFormat);
      var ExampleApiValuesMapped = apiFormat.map((item) => ({ [item.Name]: "Your data here" }));
      var NewExampleApiValues = JSON.stringify(Object.assign({}, ...ExampleApiValuesMapped), null, 2);
      setApiExamplePoints(NewExampleApiValues);
    }
    setOpen(!open);
  };

  // Function to highlight search text
  const highlightText = (text, searchValue) => {
    if (!searchValue || !text || searchValue.trim() === "") return text;

    const trimmedSearchValue = searchValue.trim();
    const regex = new RegExp(`(${trimmedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === trimmedSearchValue.toLowerCase()) {
        return (
          <mark key={index} style={{ backgroundColor: "#C3996B", color: "#F8F8F8", padding: "0", borderRadius: "2px" }}>
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  return (
    <React.Fragment>
      <tr className={classes.table__row}>
        <td>
          <IconButton 
            style={{ padding: "4px", minWidth: "auto" }} 
            onClick={handleOpen}
            size="small"
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </IconButton>
        </td>
        <td>{highlightText(row.name, props.searchValue)}</td>
        <td>{getApiTypeIcon(row.externalApiTypeId)}</td>
        <td>
          {row.externalApiTypeId === externalApiTypes.ENUM && row.externalApiEnumBehaviorTypeId ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {row.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR ? (
                <><Anchor size={16} style={{ marginRight: "4px" }} /> Anchor</>
              ) : (
                <><Pin size={16} style={{ marginRight: "4px" }} /> Sticky</>
              )}
            </div>
          ) : (
            <Minus size={16} style={{ opacity: 0.4 }} />
          )}
        </td>
        <td className={classes.descriptionCell} title={row.description}>
          {row.description}
        </td>
        <td>{row.companyName}</td>
        <td style={{ textAlign: "right" }} className={classes.implementationsCell}>
          <Tooltip
            content={
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                {row.registrationPoints.length > 0 &&
                  row.registrationPoints.map((point, index) => (
                    <div key={index} style={{ display: "flex", gap: "8px", alignItems: "center", height: "fit-content" }}>
                      <div style={{ width: "2px", borderRadius: "2px", height: "36px", backgroundColor: "#C3996B", display: "inline-block" }} />
                      <div style={{ display: "flex", flexDirection: "column", gap: "0px", alignItems: "flex-start" }}>
                        <p style={{ fontSize: "1rem" }}>{point.name}</p>
                        <p style={{ fontSize: ".875rem", opacity: "0.8" }}>{point.projectName}</p>
                      </div>
                    </div>
                  ))}
                {row.registrationPoints.length === 0 && <p style={{ fontSize: "1rem", opacity: "0.8" }}>No implementations</p>}
              </div>
            }
            placement="top"
          >
            <span>{row.registrationPoints.length}</span>
          </Tooltip>
        </td>
        <td>
          <div className={classes.buttonContainer}>
            <button 
              className={classes.iconButton}
              onClick={() => {
                handleOpenToast();
                navigator.clipboard.writeText(apiHelper.ComposeUri(apiHelper.EXTERNALAPI) + row.apiKey);
              }}
            >
              <Link className={classes.iconButton__icon} />
            </button>
            <EditExternalApisButton
              {...props}
              uniqueId={row.uniqueId}
              data={{
                externalApiName: row.name,
                externalApiDescription: row.description,
                externalApiCompanyId: row.companyId,
                externalApiFormat: row.apiFormat,
                externalApiKey: row.apiKey,
                externalApiId: row.id,
              }}
            />
          </div>
        </td>
      </tr>

      {open && (
        <tr className={classes.table__row}>
          <td colSpan="8" style={{ padding: "0", border: "none" }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, marginTop: 2 }}>
                {isAnchorOrSticky ? (
                  <>
                    <Label>
                      {row.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR
                        ? "Anchor API"
                        : "Sticky API"}
                    </Label>
                    <div className={classes.behaviorExplanation}>
                      <p>This API does not use a standard API format. Assign it to a project to generate a <strong>QR code for each option</strong> in the enum. Measurements are done by scanning these QR codes.</p>
                      {row.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR ? (
                        <p>As an <strong>anchor</strong>, scanning a QR code sets a <strong>context</strong> for that device. This context remains active until a different anchor is scanned. Values are defined once at the API level and can be reused across multiple projects.</p>
                      ) : (
                        <p>As a <strong>sticky</strong> value, scanning a QR code <strong>attaches that value to the current session</strong>. It stays linked to all subsequent actions, independent of other contexts like anchors.</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Label>API Format Example</Label>
                    <div className={classes.ApiContainer}>
                      <TextField 
                        fullWidth 
                        disabled 
                        multiline 
                        value={apiExamplePoints} 
                        label={apiExamplePoints ? " " : "API Example"} 
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#F8F8F8',
                            color: '#0C0E29',
                            '& fieldset': {
                              border: 'none',
                            },
                            '&:hover fieldset': {
                              border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                              border: 'none',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#F8F8F8',
                              color: '#0C0E29',
                              WebkitTextFillColor: '#0C0E29',
                            },
                          },
                        }}
                      />
                      {apiExamplePoints !== "" ? (
                        <Button
                          size="small"
                          variant="light"
                          onClick={handleCopyFormat}
                          startIcon={<Copy />}
                          style={{ position: "absolute", top: "8px", right: "8px", zIndex: 1 }}
                        >
                          Copy
                        </Button>
                      ) : (
                        <Button 
                          disabled 
                          size="small" 
                          variant="light" 
                          onClick={handleCopyFormat} 
                          startIcon={<Copy />}
                          style={{ position: "absolute", top: "8px", right: "8px", zIndex: 1 }}
                        >
                          Copy
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </Box>
            </Collapse>
          </td>
        </tr>
      )}
      <Snackbar open={openToast} autoHideDuration={3000} onClose={handleCloseToast} message="Copied to clipboard" />
    </React.Fragment>
  );
}
// Helper component for sortable column headers
const SortableHeader = ({ children, sortKey, currentSortColumn, currentSortOrder, onSort, className = "" }) => {
  const classes = useStyles();
  const isActive = currentSortColumn === sortKey;
  
  const handleHeaderClick = () => {
    onSort(sortKey);
  };
  
  return (
    <th 
      className={`${classes.sortableHeader} ${className}`}
      onClick={handleHeaderClick}
    >
      <div className={classes.headerContent}>
        <span>{children}</span>
        <div className={classes.sortIcons}>
          <ChevronUp 
            size={14} 
            className={`${classes.sortIcon} ${isActive && currentSortOrder === 'ASC' ? classes.activeSortIcon : ''}`}
          />
          <ChevronDown 
            size={14} 
            className={`${classes.sortIcon} ${isActive && currentSortOrder === 'DESC' ? classes.activeSortIcon : ''}`}
          />
        </div>
      </div>
    </th>
  );
};

export default function TableExternalApi(props) {
  const classes = useStyles();
  
  return (
    <React.Fragment>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.table__header}>
            <tr>
              <th style={{ width: "48px" }}></th>
              {externalApiTableConfig.columns.map((col) =>
                col.sortable ? (
                  <SortableHeader
                    key={col.key}
                    sortKey={col.key}
                    currentSortColumn={props.sortColumn}
                    currentSortOrder={props.sortOrder}
                    onSort={props.handleSorting}
                    style={col.align === "right" ? { textAlign: "right" } : undefined}
                  >
                    {col.label}
                  </SortableHeader>
                ) : (
                  <th key={col.key}>{col.label}</th>
                )
              )}
              <th style={{ minWidth: "16px" }} />
            </tr>
          </thead>
          <tbody className={classes.table__body}>
            {props.loading ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "32px" }}>
                  <div className={classes.loadingContainer}>
                    <div className={classes.spinner}></div>
                    <span>Loading APIs...</span>
                  </div>
                </td>
              </tr>
            ) : Array.isArray(props.rows) && props.rows.length > 0 ? (
              props.rows.map((row, index) => (
                <Row {...props} row={row} index={index} key={row.id || index} />
              ))
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "32px" }}>
                  No APIs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
    overflowY: "visible",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",
      cursor: "pointer",

      "&:first-child": {
        borderTopLeftRadius: "8px",
        cursor: "default",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "0px",
        padding: "0px",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap", // Prevent wrapping for all cells
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 200, // Default max width for all cells

        borderRight: "1px solid #EDEDED",

        "&:last-child": {
          borderRight: "none",
          width: "fit-content",
          padding: "8px",
        },
      },
    },
  },

  descriptionCell: {
    maxWidth: 300,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
  },

  implementationsCell: {
    overflow: "visible !important",
    position: "relative",
    zIndex: 1,
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },

  sortableHeader: {
    cursor: "pointer !important",
    userSelect: "none",
    
    "&:hover": {
      backgroundColor: "#D0D0D0 !important",
    },
  },

  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
  },

  sortIcons: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
  },

  sortIcon: {
    color: "#999999",
    strokeWidth: "2px",
    transition: "color 0.2s ease",
  },

  activeSortIcon: {
    color: "#4654FB !important",
  },

  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: "#666666",
    fontSize: "16px",
  },

  spinner: {
    width: "20px",
    height: "20px",
    border: "2px solid #f3f3f3",
    borderTop: "2px solid #4654FB",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": { 
      transform: "rotate(0deg)" 
    },
    "100%": { 
      transform: "rotate(360deg)" 
    },
  },

  ApiContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
  },

  behaviorExplanation: {
    backgroundColor: "#F8F8F8",
    borderRadius: "4px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: "#0C0E29",
    fontSize: "14px",
    lineHeight: "1.5",
    "& p": {
      margin: 0,
    },
  },
}));
